import { AdminFeature } from '@website/components/admin/AdminFeature';
import { GetHostDashboardDataQuery } from '@website/types/graphqlOperations';
import Link from 'next/link';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Row,
} from 'react-bootstrap';
import { HostPaymentDetails } from './paymentDetails/HostPaymentDetails';
import AddressFieldsView from './utils/AddressFieldsView';
import SettingItemRow from './utils/SettingItemRow';

interface HostProfileDetailsProps {
  host: GetHostDashboardDataQuery['hostByURL'];
  eventKey: string;
}

export function HostProfileDetails({
  host,
  eventKey,
}: HostProfileDetailsProps) {
  return (
    <Accordion.Item eventKey={eventKey}>
      <Accordion.Header>Host Profile</Accordion.Header>
      <Accordion.Body>
        <>
          <Card.Body>
            <Row>
              <AdminFeature>
                <SettingItemRow fieldName="Host ID" value={host.id} />
              </AdminFeature>
              <SettingItemRow fieldName="Name" value={host.name} />
              <SettingItemRow fieldName="About" value={host.about} />
              <SettingItemRow
                fieldName="Contact Name"
                value={host.contact?.contactName ?? ''}
              />
              <SettingItemRow
                fieldName="Email"
                value={host.contact?.email ?? ''}
              />
              <SettingItemRow
                fieldName="Phone"
                value={host.contact?.phone ?? ''}
              />
              <SettingItemRow fieldName="Website" value={host.website ?? ''} />
              <SettingItemRow
                fieldName="Facebook"
                value={host.facebookPage ?? ''}
              />
              <SettingItemRow
                fieldName="Twitter"
                value={host.twitterPage ?? ''}
              />
            </Row>
          </Card.Body>
          <Card.Body>
            <div className="d-grid gap-2">
              <Link
                href="/hosts/dashboard/[urlSlug]/edit/profile"
                as={`/hosts/dashboard/${host.urlSlug}/edit/profile`}
                passHref
                legacyBehavior
              >
                <Button role="button" variant="primary" size="lg">
                  Update
                </Button>
              </Link>
            </div>
          </Card.Body>
        </>
      </Accordion.Body>
    </Accordion.Item>
  );
}

interface HostBusinessDetailsProps {
  host: GetHostDashboardDataQuery['hostByURL'];
  eventKey: string;
}

export function HostBusinessDetails({
  host,
  eventKey = '1',
}: HostBusinessDetailsProps) {
  const isBusinessInfoSet = host.legalName && host.businessAddress;

  return (
    <Accordion.Item eventKey={eventKey}>
      <Accordion.Header>
        Official Business Information{' '}
        {!isBusinessInfoSet && (
          <span className="fw-bold lead  ">
            <Badge pill bg="danger" className="ms-2">
              Incomplete
            </Badge>
          </span>
        )}
      </Accordion.Header>
      <Accordion.Body>
        {isBusinessInfoSet ? (
          <>
            <Card.Body>
              <Row>
                <SettingItemRow
                  fieldName="Legal Name"
                  value={host.legalName ?? ''}
                />
                <SettingItemRow
                  fieldName="Company Registration Number"
                  value={host.companyRegistrationNumber ?? ''}
                />
              </Row>
              <Row>
                <Col xs={12}>
                  <h4>Address</h4>
                </Col>
              </Row>
              {host.businessAddress && (
                <AddressFieldsView address={host.businessAddress} />
              )}
            </Card.Body>
          </>
        ) : (
          <Card.Body>
            <Alert color="warning" variant="secondary">
              You haven't added legal business information
            </Alert>
          </Card.Body>
        )}
        <Card.Body>
          <div className="d-grid gap-2">
            <Link
              href="/hosts/dashboard/[urlSlug]/edit/business"
              as={`/hosts/dashboard/${host.urlSlug}/edit/business`}
              passHref
              legacyBehavior
            >
              <Button role="button" variant="primary" size="lg">
                Update
              </Button>
            </Link>
          </div>
        </Card.Body>
      </Accordion.Body>
    </Accordion.Item>
  );
}

interface HostDashboardContentProps {
  host: GetHostDashboardDataQuery['hostByURL'];
}

export default function HostDashboardSettings({
  host,
}: HostDashboardContentProps) {
  return (
    <Accordion>
      <HostProfileDetails host={host} eventKey="0" />
      <HostBusinessDetails host={host} eventKey="1" />
      <HostPaymentDetails
        paymentInfo={host?.paymentDetails}
        eventKey="2"
        urlSlug={host.urlSlug}
      />
    </Accordion>
  );
}
